import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useIncidents } from "@/contexts/IncidentsContext";
import { useSettings } from "@/contexts/SettingsContext";
import { api } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Sun, Moon, Trash2, Plus, RotateCcw, Save, UserPlus, KeyRound, Upload, FileDown, CheckCircle2, AlertTriangle, Copy, Code2, Terminal, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA",
  "ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR",
  "PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

const ROLE_OPTIONS = [
  { value: "dispatcher", label: "Dispatcher" },
  { value: "subscriber", label: "Subscriber" },
  { value: "supervisor", label: "Supervisor" },
  { value: "admin", label: "Admin" },
];

function NewUserForm() {
  const empty = {
    first_name: "", last_name: "", address: "", telephone: "",
    email: "", area_monitoring: "", username: "",
    password: "psa2025", role: "dispatcher",
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState([]);

  const loadUsers = async () => {
    try { const r = await api.get("/admin/users"); setUsers(r.data || []); }
    catch { /* not admin */ }
  };
  useEffect(() => { loadUsers(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.area_monitoring) {
      toast.error("First name, last name, email, and area monitoring are required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/users", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        address: form.address.trim() || null,
        telephone: form.telephone.trim() || null,
        email: form.email.trim().toLowerCase(),
        area_monitoring: form.area_monitoring.toUpperCase(),
        username: form.username.trim().toUpperCase() || null,
        password: form.password || "psa2025",
        role: form.role,
      });
      toast.success(`Created ${r.data.username} (${r.data.role})${r.data.default_password_assigned ? " · default password psa2025 — must change on first login" : ""}`);
      setForm(empty);
      loadUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create user");
    } finally { setBusy(false); }
  };

  const removeUser = async (id, name) => {
    if (!window.confirm(`Delete user ${name}?`)) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("User deleted"); loadUsers(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <Card data-testid="new-user-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><UserPlus className="w-4 h-4 text-primary" /> Create New User</CardTitle>
        <CardDescription>Admins can provision dispatcher, subscriber, supervisor, or admin accounts. Default password is <span className="font-mono text-primary">psa2025</span> — users must change it at first login.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid sm:grid-cols-2 gap-3" onSubmit={submit}>
          <div>
            <Label>First Name *</Label>
            <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="nu-firstname" />
          </div>
          <div>
            <Label>Last Name *</Label>
            <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="nu-lastname" />
          </div>
          <div className="sm:col-span-2">
            <Label>Address</Label>
            <Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="nu-address" />
          </div>
          <div>
            <Label>Telephone</Label>
            <Input value={form.telephone} onChange={(e) => set("telephone", e.target.value)} data-testid="nu-phone" />
          </div>
          <div>
            <Label>Email *</Label>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="nu-email" />
          </div>
          <div>
            <Label>Area Monitoring *</Label>
            <Select value={form.area_monitoring} onValueChange={(v) => set("area_monitoring", v)}>
              <SelectTrigger data-testid="nu-state"><SelectValue placeholder="Select state / DC" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Account Type *</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="nu-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Username (optional — auto NNNN+state if blank)</Label>
            <Input value={form.username} onChange={(e) => set("username", e.target.value.toUpperCase())} placeholder="1001NJ" className="font-mono uppercase" data-testid="nu-username" />
          </div>
          <div>
            <Label>Password</Label>
            <Input value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="psa2025" className="font-mono" data-testid="nu-password" />
            <div className="text-[10px] text-muted-foreground mt-1">Leave as <span className="font-mono">psa2025</span> to require first-login change.</div>
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={busy} className="w-full font-mono tracking-widest" data-testid="nu-submit">
              <UserPlus className="w-4 h-4 mr-2" /> {busy ? "Creating…" : "CREATE USER"}
            </Button>
          </div>
        </form>

        {/* Existing users list */}
        <div className="mt-6 border-t border-border pt-4">
          <div className="font-mono text-[10px] text-muted-foreground tracking-widest mb-2">EXISTING USERS ({users.length})</div>
          <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="user-list">
            {users.map((u) => (
              <div key={u.id} className="grid grid-cols-[110px_1fr_70px_60px_auto] items-center gap-3 text-xs font-mono border border-border/40 rounded-sm p-2">
                <div className="text-primary">{u.username}</div>
                <div>
                  <div>{u.name}</div>
                  <div className="text-[10px] text-muted-foreground">{u.email}</div>
                </div>
                <div className="uppercase">{u.role}</div>
                <div className="uppercase">{u.area_monitoring || "—"}</div>
                <Button size="icon" variant="ghost" onClick={() => removeUser(u.id, u.name)} className="h-7 w-7" data-testid={`del-user-${u.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                </Button>
              </div>
            ))}
            {!users.length && <div className="text-xs text-muted-foreground">No users.</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CurrentSessionCard() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/session");
        if (alive) setInfo(data);
      } catch { /* non-fatal */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);
  // Cheap UA parsing: pull the browser + platform slug for a friendlier label.
  const s = info?.active_session || {};
  const uaRaw = s.user_agent || "";
  const uaFriendly = (() => {
    const ua = uaRaw;
    if (!ua) return "Unknown device";
    let browser = /Edg\//.test(ua) ? "Edge"
      : /Chrome\//.test(ua) ? "Chrome"
      : /Firefox\//.test(ua) ? "Firefox"
      : /Safari\//.test(ua) ? "Safari" : "Browser";
    let os = /iPhone|iPad|iPod/.test(ua) ? "iOS"
      : /Android/.test(ua) ? "Android"
      : /Mac OS X/.test(ua) ? "macOS"
      : /Windows/.test(ua) ? "Windows"
      : /Linux/.test(ua) ? "Linux" : "Unknown";
    return `${browser} on ${os}`;
  })();

  return (
    <Card data-testid="current-session-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /> Current Session</CardTitle>
        <CardDescription>
          Only one active session per account. Signing in from another device
          will kick this one immediately.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {loading && <div className="text-muted-foreground">Loading…</div>}
        {!loading && !info && <div className="text-muted-foreground italic">No session data available.</div>}
        {!loading && info && (
          <div className="space-y-1 font-mono text-xs">
            <div><span className="text-muted-foreground">Device:</span> <span data-testid="session-device">{uaFriendly}</span></div>
            <div><span className="text-muted-foreground">IP:</span> <span data-testid="session-ip">{s.ip || "—"}</span></div>
            <div><span className="text-muted-foreground">Signed in:</span> <span data-testid="session-started">{s.started_at ? new Date(s.started_at).toLocaleString() : "—"}</span></div>
            <div><span className="text-muted-foreground">Session ID:</span> <span className="text-primary">{(info.active_session_id || "").slice(0, 10)}…</span></div>
            {uaRaw && (
              <div className="mt-2 text-muted-foreground/70 text-[10px] break-all">UA: {uaRaw}</div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function WebhooksCard({ currentUser }) {
  const [hooks, setHooks] = useState([]);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [freshSecret, setFreshSecret] = useState(null); // { url, secret } shown once
  const [testing, setTesting] = useState({});

  const refresh = async () => {
    try {
      const { data } = await api.get("/webhooks");
      setHooks(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load webhooks");
    }
  };
  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const u = url.trim();
    if (!/^https?:\/\//i.test(u)) { toast.error("Enter a full http(s):// URL"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/webhooks", { url: u });
      setFreshSecret({ url: data.url, secret: data.secret });
      setUrl("");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  const del = async (id, u) => {
    if (!window.confirm(`Delete webhook for ${u}?`)) return;
    try { await api.delete(`/webhooks/${id}`); toast.success("Webhook deleted"); await refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const toggleActive = async (h) => {
    try {
      await api.patch(`/webhooks/${h.id}`, { active: !h.active });
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const sendTest = async (id) => {
    setTesting((t) => ({ ...t, [id]: true }));
    try {
      const { data } = await api.post(`/webhooks/${id}/test`);
      if (data.ok) toast.success(`Test delivered (HTTP ${data.status})`);
      else toast.error(`Test failed: ${data.error || `HTTP ${data.status}`}`);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test failed");
    } finally {
      setTesting((t) => ({ ...t, [id]: false }));
    }
  };

  const copy = async (v) => { try { await navigator.clipboard.writeText(v); toast.success("Copied"); } catch { toast.error("Copy failed"); } };

  return (
    <Card data-testid="webhooks-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Code2 className="w-4 h-4 text-primary" /> Outbound Webhooks</CardTitle>
        <CardDescription>
          Push every new incident, update, and page to an external URL — no WebSocket
          needed on your side. Each request carries an HMAC-SHA256 signature in the
          <span className="font-mono ml-1">X-PSA-Signature</span> header so you can verify authenticity.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={create} className="flex gap-2">
          <Input
            data-testid="webhook-url-input"
            placeholder="https://your-server.example.com/psa-webhook"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="font-mono flex-1"
          />
          <Button type="submit" disabled={busy} data-testid="webhook-create-btn">
            <Plus className="w-4 h-4 mr-1" /> {busy ? "Adding…" : "Add"}
          </Button>
        </form>

        {freshSecret && (
          <div className="rounded-sm border border-emerald-500/40 bg-emerald-500/5 p-3 space-y-2" data-testid="webhook-fresh-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-500">
                <CheckCircle2 className="w-4 h-4" /> Webhook secret — copy now, it will NOT be shown again
              </div>
              <Button size="sm" variant="ghost" onClick={() => setFreshSecret(null)}>Dismiss</Button>
            </div>
            <div className="flex items-center gap-2">
              <code data-testid="webhook-fresh-secret" className="flex-1 bg-black/40 text-emerald-300 p-2 rounded-sm text-xs break-all">{freshSecret.secret}</code>
              <Button size="sm" onClick={() => copy(freshSecret.secret)}><Copy className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="text-xs text-muted-foreground font-mono">Verify: <span className="text-primary">hmac_sha256(secret, body).hex() === header.replace('sha256=', '')</span></div>
          </div>
        )}

        <div className="space-y-2">
          <div className="text-xs font-mono text-muted-foreground tracking-widest">// ACTIVE WEBHOOKS</div>
          {hooks.length === 0 && <div className="text-sm text-muted-foreground italic">No webhooks yet.</div>}
          {hooks.map((h) => (
            <div key={h.id} data-testid={`webhook-row-${h.id}`} className={`border border-border rounded-sm p-2 space-y-1 ${h.active ? "" : "opacity-60"}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-mono text-sm truncate">{h.url}</div>
                  <div className="text-[11px] text-muted-foreground font-mono">
                    events: {(h.events || []).join(", ") || "all"}
                    {h.last_delivery_at ? ` · last: ${new Date(h.last_delivery_at).toLocaleString()} (HTTP ${h.last_status ?? "err"})` : " · never delivered"}
                    {h.last_error ? ` · err: ${h.last_error}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch data-testid={`webhook-active-${h.id}`} checked={!!h.active} onCheckedChange={() => toggleActive(h)} />
                  <Button size="sm" variant="outline" disabled={!!testing[h.id]} onClick={() => sendTest(h.id)} data-testid={`webhook-test-${h.id}`}>
                    {testing[h.id] ? "…" : "Test"}
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => del(h.id, h.url)} data-testid={`webhook-delete-${h.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-xs text-muted-foreground space-y-1">
          <div>Delivered events: <span className="font-mono text-primary">incident.new · incident.update · incident.page · test</span></div>
          <div>Timeout: 5s per delivery. No retries — failures are logged in the row above.</div>
        </div>
      </CardContent>
    </Card>
  );
}

function ApiKeysCard({ currentUser }) {
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "admin_dispatcher";
  const [keys, setKeys] = useState([]);
  const [users, setUsers] = useState([]);
  const [name, setName] = useState("");
  const [targetUserId, setTargetUserId] = useState(currentUser?.id || "");
  const [busy, setBusy] = useState(false);
  const [freshKey, setFreshKey] = useState(null); // {key, prefix, name} — shown once

  const refresh = async () => {
    try {
      const { data } = await api.get("/api-keys");
      setKeys(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load API keys");
    }
  };
  useEffect(() => { refresh(); }, []);
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try { const r = await api.get("/admin/users"); setUsers(r.data || []); }
      catch { /* non-fatal */ }
    })();
  }, [isAdmin]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Give the key a name (e.g. Voice Dispatch Radio Bridge)"); return; }
    setBusy(true);
    try {
      const body = { name: name.trim() };
      if (isAdmin && targetUserId && targetUserId !== currentUser?.id) body.user_id = targetUserId;
      const { data } = await api.post("/api-keys", body);
      setFreshKey({ key: data.key, prefix: data.prefix, name: data.name, user_username: data.user_username });
      setName("");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create key");
    } finally { setBusy(false); }
  };

  const revoke = async (id, prefix) => {
    if (!window.confirm(`Revoke key ${prefix}? This cannot be undone.`)) return;
    try {
      await api.delete(`/api-keys/${id}`);
      toast.success("Key revoked");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to revoke");
    }
  };

  const copyKey = async (val) => {
    try { await navigator.clipboard.writeText(val); toast.success("Copied to clipboard"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <Card data-testid="api-keys-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><KeyRound className="w-4 h-4 text-primary" /> API Keys</CardTitle>
        <CardDescription>
          Long-lived bearer tokens for server-to-server integrations. A key inherits the role of the
          user it is issued to. Send it as <span className="font-mono">Authorization: Bearer psa_live_…</span>
          on any <span className="font-mono">/api</span> endpoint.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={create} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Key Name</Label>
              <Input
                data-testid="apikey-name-input"
                placeholder="e.g. Voice Dispatch Radio Bridge"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            {isAdmin && (
              <div>
                <Label>Issued To</Label>
                <Select value={targetUserId} onValueChange={setTargetUserId}>
                  <SelectTrigger data-testid="apikey-user-select"><SelectValue placeholder="Select a user" /></SelectTrigger>
                  <SelectContent>
                    {(users || []).map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.username} — {u.name} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <Button type="submit" disabled={busy} data-testid="apikey-create-btn" className="w-full md:w-auto">
            <Plus className="w-4 h-4 mr-1" /> {busy ? "Generating…" : "Generate Key"}
          </Button>
        </form>

        {freshKey && (
          <div className="rounded-sm border border-emerald-500/40 bg-emerald-500/5 p-3 space-y-2" data-testid="apikey-fresh-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-500">
                <CheckCircle2 className="w-4 h-4" /> New key — copy it now, it will NOT be shown again
              </div>
              <Button size="sm" variant="ghost" onClick={() => setFreshKey(null)} data-testid="apikey-fresh-dismiss">Dismiss</Button>
            </div>
            <div className="flex items-center gap-2">
              <code data-testid="apikey-fresh-value" className="flex-1 bg-black/40 text-emerald-300 p-2 rounded-sm text-xs break-all">{freshKey.key}</code>
              <Button size="sm" onClick={() => copyKey(freshKey.key)} data-testid="apikey-fresh-copy">
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
            <div className="text-xs text-muted-foreground font-mono">
              {freshKey.name} • acts as {freshKey.user_username}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div className="text-xs font-mono text-muted-foreground tracking-widest">// ACTIVE KEYS</div>
          {keys.length === 0 && (
            <div className="text-sm text-muted-foreground italic">No keys yet.</div>
          )}
          {keys.map((k) => (
            <div key={k.id} data-testid={`apikey-row-${k.id}`} className={`flex items-center justify-between border border-border rounded-sm p-2 ${k.revoked ? "opacity-50" : ""}`}>
              <div className="min-w-0">
                <div className="font-mono text-sm flex items-center gap-2">
                  <span className="text-primary">{k.prefix}…</span>
                  <span className="text-xs text-muted-foreground">{k.name}</span>
                  {k.revoked && <span className="text-[10px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded-sm">REVOKED</span>}
                </div>
                <div className="text-[11px] text-muted-foreground font-mono">
                  {k.user_username} · {k.user_role} · created {new Date(k.created_at).toLocaleString()}
                  {k.last_used_at ? ` · last used ${new Date(k.last_used_at).toLocaleString()}` : " · never used"}
                </div>
              </div>
              {!k.revoked && (
                <Button size="sm" variant="destructive" onClick={() => revoke(k.id, k.prefix)} data-testid={`apikey-revoke-${k.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function IntegrationDocsCard() {
  const base = (typeof window !== "undefined" ? window.location.origin : "").replace(/\/$/, "");
  const apiRoot = `${base}/api`;
  const curlCreate = `curl -X POST '${apiRoot}/incidents' \\
  -H 'Authorization: Bearer psa_live_YOUR_KEY_HERE' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "call_type": "MVA - Motor Vehicle Accident",
    "address": "5th Ave & 42nd St",
    "city": "New York",
    "state": "NY",
    "zip_code": "10018",
    "priority": "high",
    "branch": "EMS",
    "box_number": "",
    "notes": "2-car collision, injuries reported"
  }'`;
  const curlUpdate = `curl -X PUT '${apiRoot}/incidents/{incident_id}' \\
  -H 'Authorization: Bearer psa_live_YOUR_KEY_HERE' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "update_message": "PD on scene, roadway closed EB",
    "repage": true
  }'`;
  const wsUrl = `${base.replace(/^http/, "ws")}/api/ws`;
  const wsSnippet = `// Subscribe to live incident broadcasts
const ws = new WebSocket('${wsUrl}');
ws.onmessage = (e) => {
  const msg = JSON.parse(e.data);
  if (msg.type === 'incident_new') console.log('NEW JOB', msg.incident);
  if (msg.type === 'incident_update') console.log('UPDATE', msg.incident);
};`;
  const webhookVerify = `// Verify an inbound webhook (Node)
import crypto from 'crypto';
function verify(rawBody, headerSig, secret) {
  const expected = 'sha256=' + crypto.createHmac('sha256', secret).update(rawBody).digest('hex');
  return crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(headerSig));
}
// events: incident.new · incident.update · incident.page · test
// headers: X-PSA-Event, X-PSA-Signature, X-PSA-Delivery`;

  const copy = async (val, label) => {
    try { await navigator.clipboard.writeText(val); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed"); }
  };

  const renderBlock = (title, body, testid) => (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-mono text-muted-foreground tracking-widest">{title}</div>
        <Button size="sm" variant="ghost" onClick={() => copy(body, title)} data-testid={`${testid}-copy`}>
          <Copy className="w-3.5 h-3.5" />
        </Button>
      </div>
      <pre data-testid={testid} className="bg-black/40 text-emerald-300 p-3 rounded-sm text-xs overflow-x-auto whitespace-pre-wrap break-all">{body}</pre>
    </div>
  );

  return (
    <Card data-testid="integration-docs-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Code2 className="w-4 h-4 text-primary" /> Integration Docs</CardTitle>
        <CardDescription>
          Wire your external system (voice dispatch, CAD gateway, CSV poller, etc.) into Public Safety
          Alerts. All calls use bearer auth and stream results over WebSocket in real time.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <div className="text-sm font-semibold mb-1 flex items-center gap-2"><Terminal className="w-4 h-4" /> Auth</div>
          <p className="text-sm text-muted-foreground">
            Add the header <span className="font-mono text-primary">Authorization: Bearer psa_live_&lt;key&gt;</span> to every request.
            No cookies needed. The key inherits the role of the user it was issued to — a dispatcher key can create incidents,
            an admin key can do everything an admin can.
          </p>
        </div>

        {renderBlock("// CREATE INCIDENT — POST /api/incidents", curlCreate, "docs-curl-create")}

        {renderBlock("// POST UPDATE / RE-PAGE — PUT /api/incidents/{id}", curlUpdate, "docs-curl-update")}

        {renderBlock("// LIVE FEED — WS /api/ws", wsSnippet, "docs-ws-snippet")}

        {renderBlock("// WEBHOOK VERIFY — HMAC-SHA256", webhookVerify, "docs-webhook-verify")}

        <div className="text-xs text-muted-foreground space-y-1">
          <div><span className="font-mono text-primary">priority</span>: one of <span className="font-mono">low | medium | high | critical</span></div>
          <div><span className="font-mono text-primary">branch</span>: one of <span className="font-mono">FIRE | EMS | POLICE</span> (Fire also requires <span className="font-mono">box_number</span>)</div>
          <div><span className="font-mono text-primary">lat / lon</span>: optional — if omitted, the server geocodes the address via Google.</div>
          <div>Successful response includes <span className="font-mono">incident_number</span>, <span className="font-mono">page_number</span>, and the fully-formatted radio line used for TTS.</div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [nw2, setNw2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw !== nw2) { toast.error("Passwords don't match"); return; }
    if (nw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password changed");
      setCur(""); setNw(""); setNw2("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password");
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="change-password-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><KeyRound className="w-4 h-4 text-primary" /> Change Password</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="space-y-3" onSubmit={submit}>
          <div><Label>Current Password</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cp-current" /></div>
          <div><Label>New Password</Label><Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="cp-new" /></div>
          <div><Label>Confirm New Password</Label><Input type="password" value={nw2} onChange={(e) => setNw2(e.target.value)} data-testid="cp-confirm" /></div>
          <Button type="submit" disabled={busy} className="w-full" data-testid="cp-submit">{busy ? "Saving…" : "Update Password"}</Button>
        </form>
      </CardContent>
    </Card>
  );
}

function genId(label) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

// ─── Call Types Bulk Importer ───────────────────────────────────────────────
// Accepts .xlsx / .xls / .csv. Recognizes columns (case-insensitive):
//   "call type" | "type" | "label"  →  label
//   "branch"                        →  branch (matched against known branch ids/labels)
//   "priority"                      →  priority (matched against known priority ids/labels)
// Extra columns are ignored. Empty rows are skipped.
function CallTypeImportDialog({ open, onOpenChange, branches, priorities, currentItems, onImport }) {
  const [rows, setRows] = useState([]);       // { label, branch, priority, valid, reason }
  const [mode, setMode] = useState("append"); // "append" | "replace"
  const [fileName, setFileName] = useState("");
  const inputRef = useRef(null);

  if (!open) return null;

  const branchIndex = new Map();
  branches.forEach((b) => {
    branchIndex.set(String(b.id).toLowerCase(), b.id);
    branchIndex.set(String(b.label).toLowerCase(), b.id);
  });
  const priorityIndex = new Map();
  priorities.forEach((p) => {
    priorityIndex.set(String(p.id).toLowerCase(), p.id);
    priorityIndex.set(String(p.label).toLowerCase(), p.id);
  });

  const parseFile = async (file) => {
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
      if (!raw.length) { toast.error("File is empty"); return; }

      // Build a case-insensitive key resolver for each row
      const resolveKey = (obj, aliases) => {
        for (const k of Object.keys(obj)) {
          if (aliases.includes(k.trim().toLowerCase())) return obj[k];
        }
        return "";
      };
      const parsed = raw.map((r, i) => {
        const label = String(resolveKey(r, ["call type", "call_type", "type", "label", "name"])).trim();
        const branchRaw = String(resolveKey(r, ["branch", "department", "dept"])).trim();
        const priorityRaw = String(resolveKey(r, ["priority", "level", "severity"])).trim();
        const branchId = branchIndex.get(branchRaw.toLowerCase()) || "";
        const priorityId = priorityIndex.get(priorityRaw.toLowerCase()) || "";
        const reasons = [];
        if (!label) reasons.push("missing call type / label");
        if (branchRaw && !branchId) reasons.push(`unknown branch "${branchRaw}"`);
        if (priorityRaw && !priorityId) reasons.push(`unknown priority "${priorityRaw}"`);
        return {
          rowNum: i + 2,  // human-friendly (header row is line 1)
          label,
          branch: branchId,
          priority: priorityId,
          valid: !!label && (!branchRaw || !!branchId) && (!priorityRaw || !!priorityId),
          reason: reasons.join(" · "),
        };
      });
      setRows(parsed);
      setFileName(file.name);
    } catch (e) {
      toast.error(`Failed to parse file: ${e.message}`);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) parseFile(f);
    e.target.value = "";
  };

  const downloadTemplate = () => {
    const template = [
      { "Call Type": "Structure Fire", "Branch": "FIRE", "Priority": "high" },
      { "Call Type": "Medical Emergency", "Branch": "EMS", "Priority": "high" },
      { "Call Type": "Traffic Accident", "Branch": "EMS", "Priority": "medium" },
      { "Call Type": "Welfare Check", "Branch": "POLICE", "Priority": "low" },
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    ws["!cols"] = [{ wch: 28 }, { wch: 14 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Call Types");
    XLSX.writeFile(wb, "call-types-template.xlsx");
  };

  const validRows = rows.filter((r) => r.valid);
  const invalidRows = rows.filter((r) => !r.valid);

  const commit = () => {
    // Build merged list
    const newItems = validRows.map((r) => ({
      id: genId(r.label),
      label: r.label,
      branch: r.branch || undefined,
      priority: r.priority || undefined,
    }));
    let merged;
    if (mode === "replace") {
      merged = newItems;
    } else {
      // Append — replace existing entries that share an id, then push new ones
      const byId = new Map(currentItems.map((c) => [c.id, c]));
      newItems.forEach((n) => byId.set(n.id, { ...byId.get(n.id), ...n }));
      merged = Array.from(byId.values());
    }
    onImport(merged);
    onOpenChange(false);
    setRows([]);
    setFileName("");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => onOpenChange(false)} data-testid="ct-import-overlay">
      <div className="bg-card border border-border rounded-md w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] text-primary tracking-widest">// BULK IMPORT</div>
            <h3 className="text-lg font-bold">Import Call Types from Excel / CSV</h3>
          </div>
          <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)} data-testid="ct-import-close">✕</Button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <Button variant="outline" onClick={downloadTemplate} data-testid="ct-download-template">
              <FileDown className="w-4 h-4 mr-1.5" /> Download Template
            </Button>
            <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" onChange={onFile} className="hidden" data-testid="ct-file-input" />
            <Button onClick={() => inputRef.current?.click()} data-testid="ct-select-file">
              <Upload className="w-4 h-4 mr-1.5" /> Select File
            </Button>
            {fileName && <span className="text-xs font-mono text-muted-foreground">{fileName}</span>}
          </div>
          <div className="text-xs text-muted-foreground leading-relaxed">
            Recognized column headers (case-insensitive): <span className="font-mono">Call Type</span> / <span className="font-mono">Label</span> / <span className="font-mono">Type</span>,{" "}
            <span className="font-mono">Branch</span>, <span className="font-mono">Priority</span>. Branch and priority values are matched against your existing lists (
            {branches.map((b) => b.id).join(", ")} • {priorities.map((p) => p.id).join(", ")}).
          </div>

          {rows.length > 0 && (
            <>
              <div className="flex items-center gap-2 border-t border-border pt-4">
                <div className="text-sm font-mono">
                  <span className="text-emerald-500">{validRows.length} valid</span>
                  {invalidRows.length > 0 && <span className="text-destructive ml-3">{invalidRows.length} problem{invalidRows.length !== 1 ? "s" : ""}</span>}
                </div>
                <div className="flex-1" />
                <Label className="text-xs">On save:</Label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger className="h-8 w-40" data-testid="ct-import-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="append">Append / update</SelectItem>
                    <SelectItem value="replace">Replace all</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="border border-border rounded-sm overflow-hidden">
                <div className="grid grid-cols-[40px_60px_1fr_100px_100px_1fr] text-[10px] font-mono uppercase tracking-widest bg-muted/30 px-2 py-1.5 border-b border-border">
                  <div>OK</div>
                  <div>ROW</div>
                  <div>CALL TYPE</div>
                  <div>BRANCH</div>
                  <div>PRIORITY</div>
                  <div>NOTES</div>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {rows.map((r, i) => (
                    <div key={i} className="grid grid-cols-[40px_60px_1fr_100px_100px_1fr] px-2 py-1 text-xs font-mono border-b border-border/40 last:border-0" data-testid={`ct-import-row-${i}`}>
                      <div>{r.valid ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <AlertTriangle className="w-3.5 h-3.5 text-destructive" />}</div>
                      <div className="text-muted-foreground">{r.rowNum}</div>
                      <div className="truncate">{r.label || <span className="text-destructive/70">—</span>}</div>
                      <div className="uppercase text-primary">{r.branch || "—"}</div>
                      <div className="uppercase">{r.priority || "—"}</div>
                      <div className="text-destructive/80 truncate">{r.reason}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="p-4 border-t border-border flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ct-import-cancel">Cancel</Button>
          <Button onClick={commit} disabled={!validRows.length} data-testid="ct-import-commit">
            Import {validRows.length ? `${validRows.length} row${validRows.length !== 1 ? "s" : ""}` : ""}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ListEditor({ title, name, items, columns, onSave, onReset, extraHeader = null }) {
  const [rows, setRows] = useState(items);
  useEffect(() => setRows(items), [items]);

  const update = (i, k, v) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const remove = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const add = () => {
    const empty = {};
    columns.forEach((c) => { empty[c.key] = c.default || ""; });
    setRows((r) => [...r, empty]);
  };
  const save = async () => {
    const sanitized = rows.map((r) => ({ ...r, id: r.id || genId(r.label || "") }));
    try { await onSave(sanitized); toast.success(`${title} saved`); }
    catch { toast.error("Save failed"); }
  };

  // Programmatic replace (used by Excel import)
  useEffect(() => {
    if (Array.isArray(extraHeader?.replaceRows)) {
      setRows(extraHeader.replaceRows);
      extraHeader.onReplaced?.();
    }
  }, [extraHeader]);

  return (
    <Card data-testid={`settings-editor-${name}`}>
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{rows.length} item{rows.length !== 1 ? "s" : ""}</CardDescription>
        </div>
        <div className="flex gap-2 flex-wrap">
          {extraHeader?.buttons}
          <Button size="sm" variant="outline" onClick={onReset} data-testid={`reset-${name}`}>
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
          </Button>
          <Button size="sm" onClick={save} data-testid={`save-${name}`}>
            <Save className="w-3.5 h-3.5 mr-1" /> Save
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div key={i} className="grid items-center gap-2" style={{ gridTemplateColumns: `${columns.map(c => c.width || '1fr').join(' ')} auto` }}>
              {columns.map((c) => (
                c.type === "select" ? (
                  <Select key={c.key} value={row[c.key] || ""} onValueChange={(v) => update(i, c.key, v)}>
                    <SelectTrigger className="h-8" data-testid={`${name}-${c.key}-${i}`}>
                      <SelectValue placeholder={c.placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {c.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : c.type === "color" ? (
                  <div key={c.key} className="flex items-center gap-2">
                    <input
                      type="color"
                      value={row[c.key] || "#888888"}
                      onChange={(e) => update(i, c.key, e.target.value)}
                      className="w-9 h-8 border border-border rounded-sm bg-transparent cursor-pointer"
                      data-testid={`${name}-${c.key}-${i}`}
                    />
                    <Input
                      value={row[c.key] || ""}
                      onChange={(e) => update(i, c.key, e.target.value)}
                      className="h-8 font-mono text-xs"
                    />
                  </div>
                ) : (
                  <Input
                    key={c.key}
                    placeholder={c.placeholder}
                    value={row[c.key] || ""}
                    onChange={(e) => update(i, c.key, e.target.value)}
                    className="h-8"
                    data-testid={`${name}-${c.key}-${i}`}
                  />
                )
              ))}
              <Button size="icon" variant="ghost" onClick={() => remove(i)} data-testid={`${name}-remove-${i}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={add} className="w-full mt-3" data-testid={`add-${name}`}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add row
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { audioOn, setAudioEnabled, volume, setVolume, ttsBranches, setTtsBranchEnabled, previewNotificationTone } = useIncidents();
  const { callTypes, branches, priorities, update, reset } = useSettings();
  const [importOpen, setImportOpen] = useState(false);
  const [importedRows, setImportedRows] = useState(null);

  const isAdmin = user?.role === "admin" || user?.role === "admin_dispatcher";
  const branchOptions = branches.map((b) => b.id);
  const priorityOptions = priorities.map((p) => p.id);

  return (
    <div className="absolute inset-0 overflow-y-auto p-6" data-testid="settings-page">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <div className="font-mono text-xs text-primary tracking-widest">// USER SETTINGS</div>
          <h1 className="text-3xl font-bold mt-1">Settings & Preferences</h1>
        </div>

        <Card>
          <CardHeader><CardTitle>Profile</CardTitle></CardHeader>
          <CardContent className="space-y-2 font-mono text-sm" data-testid="profile-info">
            <div><span className="text-muted-foreground w-32 inline-block">NAME</span> {user?.name}</div>
            <div><span className="text-muted-foreground w-32 inline-block">EMAIL</span> {user?.email}</div>
            <div><span className="text-muted-foreground w-32 inline-block">ROLE</span> <span className="uppercase text-primary">{user?.role}</span></div>
            <div><span className="text-muted-foreground w-32 inline-block">AGENCY</span> {user?.agency}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Console Preferences</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Theme</Label>
                <div className="text-sm text-muted-foreground">Tactical light or dark dispatch UI.</div>
              </div>
              <Button variant="outline" onClick={toggle} data-testid="settings-theme-btn">
                {theme === "dark" ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                {theme === "dark" ? "Light Mode" : "Dark Mode"}
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Audible Alert Tones</Label>
                <div className="text-sm text-muted-foreground">Play tones for new incidents and re-pages.</div>
              </div>
              <Switch
                data-testid="settings-audio-switch"
                checked={audioOn}
                onCheckedChange={(v) => setAudioEnabled(v)}
              />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Notification Volume</Label>
                  <div className="text-sm text-muted-foreground">Dial the tone level for your environment. Applies to FDNY/NYPD tones and TTS.</div>
                </div>
                <div className="font-mono text-sm text-primary min-w-[3rem] text-right" data-testid="settings-volume-value">
                  {audioOn ? `${volume}%` : "MUTED"}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Slider
                  value={[volume]}
                  min={0}
                  max={100}
                  step={5}
                  disabled={!audioOn}
                  onValueChange={([v]) => setVolume(v)}
                  data-testid="settings-volume-slider"
                  className="flex-1"
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!audioOn}
                  onClick={previewNotificationTone}
                  data-testid="settings-audio-preview-btn"
                >
                  Test tone
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="tts-branch-card">
          <CardHeader>
            <CardTitle>Voice Announcements (TTS)</CardTitle>
            <CardDescription>
              Choose which branches trigger a spoken announcement after the chime.
              The chime always plays for every incident — this only controls the voice.
              Saved per browser.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {[
              { key: "FIRE",    label: "Fire",    desc: "10-codes, alarms, box numbers — full FDNY-style read." },
              { key: "POLICE",  label: "Police",  desc: "10-13, robbery-in-progress, generic PD calls." },
              { key: "EMS",     label: "EMS",     desc: "Cardiac, respiratory, MVA with injuries, etc." },
              { key: "WEATHER", label: "Weather", desc: "Severe weather, tornado / flash flood warnings." },
            ].map((b) => (
              <div key={b.key} className="flex items-center justify-between">
                <div>
                  <Label className="text-base">{b.label}</Label>
                  <div className="text-sm text-muted-foreground">{b.desc}</div>
                </div>
                <Switch
                  data-testid={`tts-branch-switch-${b.key.toLowerCase()}`}
                  checked={!!ttsBranches?.[b.key]}
                  disabled={!audioOn}
                  onCheckedChange={(v) => setTtsBranchEnabled(b.key, v)}
                />
              </div>
            ))}
          </CardContent>
        </Card>
          <>
            <div className="pt-4">
              <div className="font-mono text-xs text-amber-500 tracking-widest">// ADMIN MANAGEMENT</div>
              <h2 className="text-2xl font-bold mt-1">User Management</h2>
              <p className="text-sm text-muted-foreground">Provision dispatcher, subscriber, supervisor, or admin accounts.</p>
            </div>

            <NewUserForm />

            <div className="pt-4">
              <h2 className="text-2xl font-bold mt-1">Lists Management</h2>
              <p className="text-sm text-muted-foreground">Add, edit, or remove call types, branches, and priorities used across the CAD.</p>
            </div>

            <ListEditor
              title="Call Types"
              name="call_types"
              items={callTypes}
              columns={[
                { key: "id", placeholder: "id (auto)", width: "140px" },
                { key: "label", placeholder: "Display label" },
                { key: "branch", type: "select", options: branchOptions, placeholder: "Branch", width: "130px" },
                { key: "priority", type: "select", options: priorityOptions, placeholder: "Priority", width: "130px" },
              ]}
              onSave={(items) => update("call_types", items)}
              onReset={() => reset("call_types")}
              extraHeader={{
                buttons: (
                  <Button size="sm" variant="outline" onClick={() => setImportOpen(true)} data-testid="ct-import-open">
                    <Upload className="w-3.5 h-3.5 mr-1" /> Import Excel/CSV
                  </Button>
                ),
                replaceRows: importedRows,
                onReplaced: () => setImportedRows(null),
              }}
            />

            <ListEditor
              title="Branches"
              name="branches"
              items={branches}
              columns={[
                { key: "id", placeholder: "ID (e.g. PD)", width: "120px" },
                { key: "label", placeholder: "Label" },
                { key: "color", type: "color", placeholder: "#hex", width: "180px" },
              ]}
              onSave={(items) => update("branches", items)}
              onReset={() => reset("branches")}
            />

            <ListEditor
              title="Priorities"
              name="priorities"
              items={priorities}
              columns={[
                { key: "id", placeholder: "id (low/medium/high)", width: "180px" },
                { key: "label", placeholder: "Display label" },
                { key: "color", type: "color", placeholder: "#hex", width: "180px" },
              ]}
              onSave={(items) => update("priorities", items)}
              onReset={() => reset("priorities")}
            />
          </>
        )}

        <ChangePasswordCard />

        <CurrentSessionCard />

        <ApiKeysCard currentUser={user} />

        <WebhooksCard currentUser={user} />

        <IntegrationDocsCard />

        <Card>
          <CardHeader><CardTitle>Session</CardTitle></CardHeader>
          <CardContent>
            <Button variant="destructive" onClick={logout} data-testid="settings-logout-btn">Sign Out</Button>
          </CardContent>
        </Card>
      </div>

      {isAdmin && (
        <CallTypeImportDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          branches={branches}
          priorities={priorities}
          currentItems={callTypes}
          onImport={(merged) => setImportedRows(merged)}
        />
      )}
    </div>
  );
}
